import React, { Dispatch, FormEvent, SetStateAction, useEffect, useState } from 'react';
import styles from './addModal.module.scss';
import { Dictionary, SchoolGroup } from '../../../../types';
import Modal from '../../../../components/modal/Modal';
import Button from '../../../../components/button/Button';
import { TextInput, TextInputValue } from '../../../../components/text-input/TextInput';
import { isEmptyObject, validateIsEmpty } from '../../../../utils/validation';
import { gql, useMutation } from '@apollo/client';
import { GroupsQuery, GroupsResponse } from '../SuperGroups';

type Props = {
  open: boolean;
  deleteOption: boolean;
  onClose: () => void;
  group?: SchoolGroup;
};

const emptyErrors = {
  name: '',
  courses: '',
};

const addGroupMutation = gql`
  mutation addGroup($input: AddGroupInput!) {
    addGroup(input: $input) {
      id
      name
    }
  }
`;

type AddGroupResponse = {
  addGroup: Dictionary;
}

const editGroupMutation = gql`
  mutation editGroup($input: EditGroupInput!) {
    editGroup(input: $input) {
      id
      name
    }
  }
`;

type EditGroupResponse = {
  editGroup: Dictionary;
}

const deleteGroupMutation = gql`
  mutation deleteGroup($id: String!) {
    deleteGroup(id: $id)
  }
`;

type DeleteGroupResponse = {
  deleteGroup: boolean;
}

function AddModal(props: Props) {
  const { open, onClose, group, deleteOption } = props;
  useEffect(() => {
    setName(group?.name ?? '');
  }, [group]);

  const isEdit = !!group;

  const [addGroup, { loading }] = useMutation<AddGroupResponse>(addGroupMutation, {
    onError(e) {
      setBeError(e.message);
    },
    update(cache, response) {
      if (response.data?.addGroup) {
        const groupsResponse = cache.readQuery<GroupsResponse>({
          query: GroupsQuery
        });
        cache.writeQuery({
          query: GroupsQuery,
          data: {
            listGroups: [...(groupsResponse?.listGroups || []), response.data.addGroup],
          }
        });
      }
      handleClose();
    }
  });

  const [editGroup, { loading: editLoading }] = useMutation<EditGroupResponse>(editGroupMutation, {
    onError(e) {
      setBeError(e.message);
    },
    update(cache, response) {
      const u = response.data?.editGroup;
      if (u) {
        cache.writeFragment({
          id: cache.identify(u),
          data: {
            ...u,
          },
          fragment: gql`
              fragment GroupData on Group {
                id
                name
              }
          `,
        });
      }
      handleClose();
    }
  });

  const [deleteGroup, {loading: removeLoading}] = useMutation<DeleteGroupResponse>(deleteGroupMutation, {
    onError(err) {
      setBeError(err.message);
    },
    onCompleted() {
      onClose();
    },
    update(cache, response, {variables}) {
      if (response.data?.deleteGroup && variables) {
        const groupsResponse = cache.readQuery<GroupsResponse>({
          query: GroupsQuery
        });
        cache.writeQuery({
          query: GroupsQuery,
          data: {
            listGroups: groupsResponse?.listGroups.filter(u => u.id !== variables.id) ?? []
          }
        });
      }
      handleClose();
    }
  });

  const [name, setName] = useState('');
  const [error, setError] = useState(emptyErrors);
  const [beError, setBeError] = useState('');

  const handleChangeText = (setter: Dispatch<SetStateAction<string>>, key: keyof typeof emptyErrors) => (value: TextInputValue) => {
    setter(value as string);
    setError({
      ...error,
      [key]: '',
    });
  };

  const handleClose = () => {
    clearFields()
    onClose();
  };

  const handleDelete = () => {
    group && deleteGroup({
      variables: {
        id: group.id,
      },
    });
  }
  const clearFields = () => {
    setName('');
  };

  const validate = () => {
    const errors: any = {};

    if (validateIsEmpty(name)) {
      errors.name = 'Name is required';
    }

    return errors;
  };

  const handleSave = (e: FormEvent) => {
    e.preventDefault();
    setBeError('');

    const errors = validate();

    if (isEmptyObject(errors)) {
      addGroup({
        variables: {
          input: {
            name
          }
        }
      })
    } else {
      setError(errors);
    }
  };

  const handleEdit = (e: FormEvent) => {
    e.preventDefault();
    setBeError('');

    const errors = validate();

    if (isEmptyObject((errors))) {
      editGroup({
        variables: {
          input: {
            id: group!.id,
            name
          }
        }
      })
    } else {
      setError(errors);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      {deleteOption ?
      <div>
        <div className={styles.title}>Delete Group</div>
          <div className={styles.content}>
            <div>{`Are you sure you want to delete group ${group?.name || ''}?`}</div>
            <div className={styles.deleteAction}>
              <Button className={styles.cancelButton} disabled={removeLoading} onClick={handleClose} label={'NO, Cancel'}/>
              <Button className={styles.deleteButton} disabled={removeLoading} onClick={handleDelete} label={'YES, DELETE'}/>
            </div>
          </div>
      </div> : <div>
        <div className={styles.title}>{isEdit ? 'Edit' : 'Add'} Group Name</div>
        <form className={styles.form} onSubmit={isEdit ? handleEdit : handleSave}>
          <div className={styles.content}>
            <TextInput
              hasError={!!error.name}
              subTitle={error.name}
              placeholder={'Name'}
              inline={true}
              label={'Name'}
              id={'name'}
              value={name}
              onChange={handleChangeText(setName, 'name')}
            />
            {Boolean(beError) && <div className={styles.error}>Error: {beError}</div>}
            <div className={styles.action}>
              <div/>
              {isEdit ? <Button className={styles.button} disabled={editLoading} type={'submit'} label={'Save'}/> :
                <Button disabled={loading} className={styles.button} type={'submit'} label={'Create'}/>}
            </div>
          </div>
        </form>
      </div>
    }
    </Modal>
  );
}

export default AddModal;
